import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button, Badge, Card, CardContent } from '@/components/ui';
import {
  useWeeklyTimesheet,
  useUpdateTimeEntry,
  useSubmitWeekForApproval,
  useCreateTimeEntry,
  useRejectionHistory,
} from './hooks/useTimeEntries';
import type { ApprovalStatus, TimesheetCell } from './hooks/useTimeEntries';
import { useToast } from '@/components/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { JobSearchModal, type JobSearchResult } from '@/components/JobSearchModal';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getWeekStart(date: Date): string {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday start
  d.setDate(diff);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${dayOfMonth}`;
}

function getWeekDays(weekStart: string): string[] {
  const days: string[] = [];
  const [y, m, day] = weekStart.split('-').map(Number);
  for (let i = 0; i < 7; i++) {
    const d = new Date(y, m - 1, day + i);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const dayOfMonth = String(d.getDate()).padStart(2, '0');
    days.push(`${year}-${month}-${dayOfMonth}`);
  }
  return days;
}

function formatDayHeader(dateStr: string): { day: string; date: string } {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.toLocaleDateString('en-US', { weekday: 'short' });
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return { day, date };
}

function formatDuration(minutes: number): string {
  if (minutes === 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function parseDurationInput(value: string): number | null {
  // Supports formats: "2h 30m", "2.5", "150m", "2:30"
  const trimmed = value.trim();
  if (!trimmed) return 0;

  // HH:MM format
  const colonMatch = trimmed.match(/^(\d+):(\d{1,2})$/);
  if (colonMatch) {
    return parseInt(colonMatch[1]) * 60 + parseInt(colonMatch[2]);
  }

  // Xh Ym format
  const hmMatch = trimmed.match(/^(\d+)h\s*(\d+)m?$/i);
  if (hmMatch) {
    return parseInt(hmMatch[1]) * 60 + parseInt(hmMatch[2]);
  }

  // Xh format
  const hMatch = trimmed.match(/^(\d+(?:\.\d+)?)h$/i);
  if (hMatch) {
    return Math.round(parseFloat(hMatch[1]) * 60);
  }

  // Xm format
  const mMatch = trimmed.match(/^(\d+)m$/i);
  if (mMatch) {
    return parseInt(mMatch[1]);
  }

  // Decimal hours (e.g., "2.5" = 2h 30m)
  const decimalMatch = trimmed.match(/^(\d+(?:\.\d+)?)$/);
  if (decimalMatch) {
    return Math.round(parseFloat(decimalMatch[1]) * 60);
  }

  return null;
}

const APPROVAL_BADGE_VARIANTS: Record<
  ApprovalStatus,
  { variant: 'success' | 'warning' | 'danger'; label: string }
> = {
  approved: { variant: 'success', label: 'Approved' },
  pending: { variant: 'warning', label: 'Pending' },
  rejected: { variant: 'danger', label: 'Rejected' },
};

// ─── Components ──────────────────────────────────────────────────────────────

interface DurationCellProps {
  cell: TimesheetCell | undefined;
  onUpdate: (minutes: number) => void;
  onViewRejectionHistory?: (entryId: string) => void;
  disabled?: boolean;
}

function DurationCell({ cell, onUpdate, onViewRejectionHistory, disabled }: DurationCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const minutes = cell?.durationMinutes ?? 0;
  const status = cell?.approvalStatus;

  const handleDoubleClick = () => {
    if (disabled || status === 'approved') return;
    setInputValue(minutes > 0 ? String(minutes / 60) : '');
    setIsEditing(true);
  };

  const handleBlur = () => {
    setIsEditing(false);
    const parsed = parseDurationInput(inputValue);
    if (parsed !== null && parsed !== minutes) {
      onUpdate(parsed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  const cellBg =
    status === 'approved'
      ? 'bg-green-50 dark:bg-green-950/20'
      : status === 'rejected'
        ? 'bg-red-50 dark:bg-red-950/20'
        : '';

  if (isEditing) {
    return (
      <td className="border border-gray-200 dark:border-gray-700 p-0">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          placeholder="0h"
          className="w-full h-full px-2 py-1.5 text-center text-sm bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-400 dark:border-blue-600 outline-none"
          aria-label="Duration input"
        />
      </td>
    );
  }

  return (
    <td
      className={`border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-center text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${cellBg} relative group`}
      onDoubleClick={handleDoubleClick}
      role="gridcell"
      tabIndex={0}
      aria-label={`${formatDuration(minutes) || '0'} minutes${status === 'rejected' && cell?.rejectionComment ? `. Rejected: ${cell.rejectionComment}` : ''}`}
    >
      <span className="text-gray-900 dark:text-gray-100">{formatDuration(minutes)}</span>
      {status === 'rejected' && (
        <button
          type="button"
          className="block text-[10px] text-red-500 hover:text-red-700 underline cursor-pointer mx-auto"
          aria-label="View rejection reason"
          onClick={(e) => {
            e.stopPropagation();
            if (cell?.id && onViewRejectionHistory) onViewRejectionHistory(cell.id);
          }}
        >
          ✗ rejected
        </button>
      )}
      {status === 'approved' && (
        <span className="block text-[10px] text-green-500" aria-label="Approved">
          ✓
        </span>
      )}
      {/* Rejection reason tooltip */}
      {status === 'rejected' && cell?.rejectionComment && (
        <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block w-48 pointer-events-none">
          <div className="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded px-2 py-1.5 shadow-lg">
            <span className="font-medium">Rejected:</span> {cell.rejectionComment}
          </div>
          <div className="w-2 h-2 bg-gray-900 dark:bg-gray-100 rotate-45 mx-auto -mt-1" />
        </div>
      )}
    </td>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function TimesheetPage() {
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [rejectionHistoryEntryId, setRejectionHistoryEntryId] = useState<string | null>(null);
  const weekStart = useMemo(() => getWeekStart(currentDate), [currentDate]);
  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);

  const { data: timesheet, isLoading } = useWeeklyTimesheet(weekStart);
  const updateEntry = useUpdateTimeEntry();
  const createEntry = useCreateTimeEntry();
  const submitWeek = useSubmitWeekForApproval();
  const { toast } = useToast();
  const { can, isAdmin, isLoading: permissionsLoading } = usePermissions();
  const canApprove = isAdmin || (!permissionsLoading && can('time_entries.approve'));

  const navigateWeek = useCallback((direction: -1 | 1) => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + direction * 7);
      return d;
    });
  }, []);

  const handleCellUpdate = useCallback(
    (entryId: string | null, jobId: string, date: string, minutes: number) => {
      if (entryId && minutes > 0) {
        // Update existing entry
        updateEntry.mutate({ id: entryId, durationMinutes: minutes });
      } else if (entryId && minutes === 0) {
        // Delete the entry when duration is set to 0
        updateEntry.mutate({ id: entryId, durationMinutes: 1 }); // API requires min 1, handle via delete if needed
      } else if (!entryId && minutes > 0) {
        // Create a new entry for this job/day
        createEntry.mutate(
          { jobId, date, durationMinutes: minutes, isBillable: true },
          {
            onError: () => {
              toast({ variant: 'error', title: 'Failed to save time entry.' });
            },
          },
        );
      }
    },
    [updateEntry, createEntry, toast],
  );

  const handleSubmitForApproval = () => {
    submitWeek.mutate(weekStart, {
      onSuccess: (data) => {
        toast({
          variant: 'success',
          title: `Submitted ${data.submitted} entries for approval.`,
        });
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Failed to submit timesheet for approval.',
        });
      },
    });
  };

  const weekLabel = useMemo(() => {
    const start = new Date(weekStart + 'T00:00:00');
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }, [weekStart]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Timesheet</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Track your weekly hours by job
          </p>
        </div>
        <div className="flex items-center gap-3">
          {timesheet?.overallApprovalStatus && (
            <Badge variant={APPROVAL_BADGE_VARIANTS[timesheet.overallApprovalStatus].variant}>
              {APPROVAL_BADGE_VARIANTS[timesheet.overallApprovalStatus].label}
            </Badge>
          )}
          {canApprove && (
            <Link to="/time-tracking/approvals">
              <Button variant="ghost">Review Approvals</Button>
            </Link>
          )}
          <Button variant="outline" onClick={() => setShowAddEntry(true)}>
            + Add Entry
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitForApproval}
            disabled={
              submitWeek.isPending ||
              timesheet?.overallApprovalStatus === 'approved' ||
              timesheet?.overallApprovalStatus === 'pending'
            }
          >
            {submitWeek.isPending
              ? 'Submitting...'
              : timesheet?.overallApprovalStatus === 'pending'
                ? 'Awaiting Approval'
                : timesheet?.overallApprovalStatus === 'approved'
                  ? 'Approved'
                  : timesheet?.overallApprovalStatus === 'rejected'
                    ? 'Resubmit for Approval'
                    : 'Submit for Approval'}
          </Button>
        </div>
      </div>

      {/* Week Navigation */}
      <Card>
        <div className="flex items-center justify-between p-4">
          <Button variant="ghost" onClick={() => navigateWeek(-1)} aria-label="Previous week">
            ← Prev
          </Button>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{weekLabel}</span>
          <Button variant="ghost" onClick={() => navigateWeek(1)} aria-label="Next week">
            Next →
          </Button>
        </div>

        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" role="grid" aria-label="Weekly timesheet">
                <thead>
                  <tr>
                    <th className="border border-gray-200 dark:border-gray-700 px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800 min-w-[200px]">
                      Job
                    </th>
                    {weekDays.map((day) => {
                      const { day: dayName, date } = formatDayHeader(day);
                      return (
                        <th
                          key={day}
                          className="border border-gray-200 dark:border-gray-700 px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 min-w-[80px]"
                        >
                          <div>{dayName}</div>
                          <div className="text-[10px] text-gray-400 dark:text-gray-500">{date}</div>
                        </th>
                      );
                    })}
                    <th className="border border-gray-200 dark:border-gray-700 px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 min-w-[70px]">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {timesheet?.rows.map((row) => {
                    const rowTotal = weekDays.reduce(
                      (sum, day) => sum + (row.entries[day]?.durationMinutes ?? 0),
                      0,
                    );
                    return (
                      <tr key={row.jobId}>
                        <td className="border border-gray-200 dark:border-gray-700 px-3 py-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {row.jobTitle}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {row.clientName}
                          </div>
                        </td>
                        {weekDays.map((day) => (
                          <DurationCell
                            key={day}
                            cell={row.entries[day]}
                            onUpdate={(minutes) =>
                              handleCellUpdate(
                                row.entries[day]?.id ?? null,
                                row.jobId,
                                day,
                                minutes,
                              )
                            }
                            onViewRejectionHistory={setRejectionHistoryEntryId}
                            disabled={timesheet.overallApprovalStatus === 'approved'}
                          />
                        ))}
                        <td className="border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-center text-sm font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800">
                          {formatDuration(rowTotal)}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Empty state */}
                  {(!timesheet?.rows || timesheet.rows.length === 0) && (
                    <tr>
                      <td
                        colSpan={9}
                        className="border border-gray-200 dark:border-gray-700 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                      >
                        No time entries this week. Add entries using the timer or manual entry form.
                      </td>
                    </tr>
                  )}

                  {/* Daily totals row */}
                  {timesheet?.rows && timesheet.rows.length > 0 && (
                    <tr className="bg-gray-50 dark:bg-gray-800 font-medium">
                      <td className="border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                        Daily Total
                      </td>
                      {weekDays.map((day) => (
                        <td
                          key={day}
                          className="border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-center text-sm text-gray-900 dark:text-gray-100"
                        >
                          {formatDuration(timesheet.dailyTotals[day] ?? 0)}
                        </td>
                      ))}
                      <td className="border border-gray-200 dark:border-gray-700 px-2 py-1.5 text-center text-sm font-bold text-blue-600 dark:text-blue-400">
                        {formatDuration(timesheet.weeklyTotal)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Time Entry Form */}
      {showAddEntry && (
        <AddTimeEntryForm weekStart={weekStart} onClose={() => setShowAddEntry(false)} />
      )}

      {/* Rejection History Modal */}
      {rejectionHistoryEntryId && (
        <RejectionHistoryModal
          entryId={rejectionHistoryEntryId}
          onClose={() => setRejectionHistoryEntryId(null)}
        />
      )}
    </div>
  );
}

function RejectionHistoryModal({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const { data: history, isLoading } = useRejectionHistory(entryId);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Rejection History
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : !history || history.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
            No rejection history found.
          </p>
        ) : (
          <div className="space-y-4">
            {history.map((item, idx) => (
              <div
                key={item.id}
                className="border border-red-200 dark:border-red-900/30 rounded-lg p-3 bg-red-50 dark:bg-red-950/10"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800 dark:text-red-300">
                      {item.rejectionComment}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {item.rejectedByName && (
                        <>
                          Rejected by <span className="font-medium">{item.rejectedByName}</span>{' '}
                          ·{' '}
                        </>
                      )}
                      {new Date(item.rejectedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                    #{history.length - idx}
                  </span>
                </div>
                {item.entrySnapshot && (
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 border-t border-red-100 dark:border-red-900/20 pt-2">
                    <span className="font-medium">At time of rejection:</span>{' '}
                    {Math.floor(item.entrySnapshot.duration_minutes / 60)}h{' '}
                    {item.entrySnapshot.duration_minutes % 60}m
                    {item.entrySnapshot.description && ` — "${item.entrySnapshot.description}"`}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function AddTimeEntryForm({ weekStart, onClose }: { weekStart: string; onClose: () => void }) {
  const [jobId, setJobId] = useState('');
  const [jobLabel, setJobLabel] = useState('');
  const [date, setDate] = useState(weekStart);
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [description, setDescription] = useState('');
  const [isBillable, setIsBillable] = useState(true);
  const [showJobSearch, setShowJobSearch] = useState(false);
  const createEntry = useCreateTimeEntry();

  const handleJobSelected = (job: JobSearchResult) => {
    setJobId(job.id);
    setJobLabel(`${job.title} – ${job.clientName}`);
    setShowJobSearch(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const totalMinutes = (Number(hours) || 0) * 60 + (Number(minutes) || 0);
    if (!jobId || totalMinutes <= 0) return;

    createEntry.mutate(
      { jobId, date, durationMinutes: totalMinutes, description, isBillable },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="card w-full max-w-md">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Add Time Entry
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Job
              </label>
              <button
                type="button"
                onClick={() => setShowJobSearch(true)}
                className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm hover:border-brand-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-brand-400"
              >
                <span className={jobLabel ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}>
                  {jobLabel || 'Search & select a job...'}
                </span>
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Date
              </label>
              <input
                type="date"
                className="input-field"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Hours
                </label>
                <input
                  type="number"
                  className="input-field"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  min="0"
                  max="24"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Minutes
                </label>
                <input
                  type="number"
                  className="input-field"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  min="0"
                  max="59"
                  placeholder="0"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <input
                type="text"
                className="input-field"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What did you work on?"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="billable"
                checked={isBillable}
                onChange={(e) => setIsBillable(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="billable" className="text-sm text-gray-700 dark:text-gray-300">
                Billable
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={createEntry.isPending}>
                Save Entry
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Job Search Modal */}
      {showJobSearch && (
        <JobSearchModal
          onSelect={handleJobSelected}
          onClose={() => setShowJobSearch(false)}
          title="Select Job for Time Entry"
          defaultStatus=""
        />
      )}
    </>
  );
}
